import type {PropsWithChildren} from 'react'
import {createContext, useCallback, use, useMemo, useState} from 'react'

type MarkersDialogContextData = {
  isActionBarVisible: boolean
  showActionBar: () => void
  hideActionBar: () => void
  openDialog: (dialog: DialogType, cellId?: string | undefined, opts?: {withSuggestedChange?: boolean}) => void
  closeDialog: (dialog: DialogType) => void
  anyMenuOpen: boolean
  openContextMenuCell?: string
  openMarkersListCell?: string
  openMarkerDetailsCell?: string
  openNewConversationCell?: {cellId: string | undefined; withSuggestedChange?: boolean}
}

const MarkersDialogContext = createContext<MarkersDialogContextData | null>(null)
type DialogType = 'new-conversation' | 'marker-details' | 'marker-list' | 'context-menu'

/**
 * Manages the state of thread, comment, and annotation dialogs for a line in a diff
 */
export function MarkersDialogContextProvider({children}: PropsWithChildren<{}>) {
  // Tracks the state of the action bar visibility for this row
  const [isActionBarVisible, setIsActionBarVisible] = useState(false)
  // Tracks which dialogs are open
  const [openContextMenuCell, setOpenContextMenuCell] = useState<string | undefined>()
  const [openMarkersListCell, setOpenMarkersListCell] = useState<string | undefined>()
  const [openMarkerDetailsCell, seOpenMarkerDetailsCell] = useState<string | undefined>()
  const [openNewConversationCell, setOpenNewConversationCell] = useState<
    {cellId: string | undefined; withSuggestedChange?: boolean} | undefined
  >()

  const anyMenuOpen = useMemo(
    () => !!openContextMenuCell || !!openMarkersListCell || !!openMarkerDetailsCell || !!openNewConversationCell,
    [openContextMenuCell, openMarkerDetailsCell, openMarkersListCell, openNewConversationCell],
  )

  const showActionBar = useCallback(() => {
    setIsActionBarVisible(true)
  }, [])

  const hideActionBar = useCallback(() => {
    setIsActionBarVisible(false)
  }, [])

  const openDialog = useCallback((dialog: DialogType, cellId?: string, opts?: {withSuggestedChange?: boolean}) => {
    switch (dialog) {
      case 'context-menu':
        setOpenContextMenuCell(cellId)
        setOpenNewConversationCell(undefined)
        seOpenMarkerDetailsCell(undefined)
        setOpenMarkersListCell(undefined)
        break
      case 'new-conversation':
        setOpenNewConversationCell({cellId, withSuggestedChange: opts?.withSuggestedChange})
        setOpenContextMenuCell(undefined)
        seOpenMarkerDetailsCell(undefined)
        setOpenMarkersListCell(undefined)
        break
      case 'marker-details':
        seOpenMarkerDetailsCell(cellId)
        setOpenContextMenuCell(undefined)
        setOpenMarkersListCell(undefined)
        setOpenNewConversationCell(undefined)
        break
      case 'marker-list':
        setOpenMarkersListCell(cellId)
        setOpenContextMenuCell(undefined)
        setOpenNewConversationCell(undefined)
        seOpenMarkerDetailsCell(undefined)
        break
      default:
        break
    }
  }, [])

  const closeDialog = useCallback((dialog: DialogType) => {
    switch (dialog) {
      case 'context-menu':
        setOpenContextMenuCell(undefined)
        break
      case 'new-conversation':
        setOpenNewConversationCell(undefined)
        break
      case 'marker-details':
        seOpenMarkerDetailsCell(undefined)
        break
      case 'marker-list':
        setOpenMarkersListCell(undefined)
        break
      default:
        break
    }
  }, [])

  const contextData = useMemo(
    () => ({
      isActionBarVisible,
      showActionBar,
      hideActionBar,
      closeDialog,
      openDialog,
      anyMenuOpen,
      openContextMenuCell,
      openMarkersListCell,
      openMarkerDetailsCell,
      openNewConversationCell,
    }),
    [
      isActionBarVisible,
      showActionBar,
      hideActionBar,
      closeDialog,
      openDialog,
      anyMenuOpen,
      openContextMenuCell,
      openMarkersListCell,
      openMarkerDetailsCell,
      openNewConversationCell,
    ],
  )

  return <MarkersDialogContext value={contextData}>{children}</MarkersDialogContext>
}

/**
 * Get data related to managing the dialog states for a line in a diff
 */
export function useMarkersDialogContext(): MarkersDialogContextData {
  const contextData = use(MarkersDialogContext)
  if (!contextData) {
    throw new Error('useMarkersDialogContext must be used within a MarkersDialogContextProvider')
  }

  return contextData
}
